import {Lang} from 'yougile-lang'
import {logger, ObjUtil, Time, Util, UUID} from 'yougile-util'
import {
  AccountListResp,
  DataObjectId,
  Hub,
  rootNodeId,
  SandboxTree,
  sharingGuestAccount,
  User,
  UserAccount,
  youGileUserId
} from 'yougile-model'
import {Backend} from '@common/backend'
import {Debouncer} from '@common/debouncer'
import {showConfirm} from '@components/common/base/notifications'
import {MessageBox} from '@components/message-box'
import {Feedback} from '@extensions/feedback'
import {globals} from '@extensions/globals-base'
import {Connector} from './connector'
import {Refresh} from './refresh'
import {SharedBoardsClient} from './shared-boards-client'
import {Sync} from './sync'
import {SyncEvents} from './sync-events'

interface AccountListener {
  cb: () => void
  account: DataObjectId | 'all' | 'current'
}

const fetchAllInterval = 3 * 60 * 1000
const fetchRequested: Set<DataObjectId> = new Set()
const triggerDebouncer = new Debouncer(1000)
const fetchDebouncer = new Debouncer<DataObjectId>(1000)
fetchDebouncer.onReady((data: DataObjectId[]) => {
  Accounts.fetch(data)
})

/**
 * Allows to update UserAccount-information, retrieve other users’ accounts and
 * subscribe to changes.
 */
export class Accounts {
  private static newCompanyListenerId: UUID
  private static refreshTimerId: TimeoutHandler
  private static fetchAllTimerId: TimeoutHandler
  private static users: Map<DataObjectId, UserAccount> = new Map()

  private static userUpdateListeners: Map<UUID, AccountListener> = new Map()

  public static addListener(lnr: AccountListener): UUID {
    const uuid = Util.UUID()
    Accounts.userUpdateListeners.set(uuid, lnr)
    return uuid
  }

  public static removeListener(id: UUID): void {
    Accounts.userUpdateListeners.delete(id)
  }

  public static trigger(changed: DataObjectId[], triggerAll = false): void {
    triggerDebouncer.runButNotOften(() => {
      if (Accounts.userUpdateListeners.size > 1000) {
        logger.log('acc up', Accounts.userUpdateListeners.size)
      }
      try {
        Accounts.userUpdateListeners.forEach(lnr => {
          if (
            triggerAll ||
            lnr.account === 'all' ||
            (lnr.account === 'current' &&
              !!changed.find(uid =>
                Sync.getCurrentSandbox().isUserInCompany(uid)
              )) ||
            (lnr.account !== 'current' && changed.includes(lnr.account))
          ) {
            lnr.cb()
          }
        })
      } catch (e) {
        logger.error(e)
      }
    })
  }

  public static get(userId: DataObjectId, fetch = false): UserAccount {
    if (!Accounts.users.has(userId)) {
      if (fetch && !fetchRequested.has(userId)) {
        fetchDebouncer.process(userId)
        fetchRequested.add(userId)
      }
      return new UserAccount(userId, '_', '')
    }
    return Accounts.users.get(userId)
  }

  public static getName(
    userId?: DataObjectId,
    task?: Hub,
    fetch?: boolean
  ): string {
    if (!userId) {
      return undefined
    }
    if (userId === youGileUserId) {
      return 'YouGile'
    }
    if (userId === sharingGuestAccount.id) {
      return Lang.msg.share.guest
    }
    if (task && !!Feedback.column && task.location === Feedback.column.id) {
      return 'YouGile Support'
    } else if (
      task &&
      Feedback.getFeedbackUserData(task) &&
      Feedback.getFeedbackUserData(task).name
    ) {
      return Feedback.getFeedbackUserData(task).name
    } else {
      const account = Accounts.get(userId, fetch)
      if (account?.realName && account.realName !== '_') {
        return account.realName
      } else {
        const user = Sync.getProfile().getUser(userId)
        const userFromCompany = Sync.getCurrentSandbox()?.getUser(userId)
        return user?.email || userFromCompany?.email || '—'
      }
    }
  }

  public static setDnd(hours: number): void {
    const newAcc = Accounts.get(globals.currentUserId)
    newAcc.dndUntil = hours > 0 ? Time.now() + hours * 60 * 60000 : 0
    void Accounts.update(newAcc)
  }

  public static doTypingAt(hubId: DataObjectId): void {
    const acc = Accounts.users.get(globals.currentUserId)
    if (!acc) {
      return
    }
    const minUpdateInterval = 5000
    const now = Time.now()
    if (
      acc?.typing &&
      acc.typing[0] === hubId &&
      acc.typing[1] > now - minUpdateInterval
    ) {
      return
    }
    acc.typing = [hubId, now]
    void Accounts.update(acc)
  }

  public static stopTyping(): void {
    const acc = Accounts.users.get(globals.currentUserId)
    if (!acc || !acc.typing) {
      return
    }
    acc.typing = undefined
    void Accounts.update(acc)
  }

  public static getAllTypingAt(hubId: DataObjectId): DataObjectId[] {
    const now = Time.now()
    const timeout = 12000
    return Accounts.accounts()
      .filter(
        acc =>
          acc.typing && acc.typing[0] === hubId && acc.typing[1] > now - timeout
      )
      .map(acc => acc.id)
  }

  public static update(account: UserAccount): Promise<void> {
    return Connector.request('account-up', {account})
      .then(() => {
        Accounts.trigger([account.id])
      })
      .catch(res => logger.warn('account-up', res.error))
  }

  private static onAccountsFetched(res: AccountListResp): void {
    const {accounts, notFound} = res
    if (accounts) {
      let changed = false
      Object.keys(accounts).forEach((uid: DataObjectId) => {
        if (
          !Accounts.users.has(uid) ||
          !ObjUtil.deepEqual(Accounts.users.get(uid), accounts[uid], false)
        ) {
          Accounts.users.set(uid, accounts[uid])
          changed = true
        }
      })
      if (changed) {
        Accounts.trigger(Object.keys(accounts) as DataObjectId[])
      }
    }
    if (
      !globals.silentMode &&
      notFound &&
      globals.currentUserId &&
      notFound.includes(globals.currentUserId)
    ) {
      logger.log('Account not found, creating…')
      let acc: UserAccount = Accounts.users.get(globals.currentUserId)
      if (!acc) {
        const user = Sync.getProfile().get(globals.currentUserId) as User
        acc = new UserAccount(globals.currentUserId, user.email, '')
      }
      acc.status = 'online'
      acc.lastActivity = Time.now()
      void Accounts.update(acc)
    }
  }

  public static fetch(userIds: DataObjectId[]): void {
    Connector.request('account-list', {userIds})
      .then(Accounts.onAccountsFetched)
      .catch(res => logger.warn('account-list', res.error))
  }

  public static addUsers(userIds: DataObjectId[]): void {
    Accounts.fetch(userIds)
    Connector.request('account-subscribe', {userIds}).catch(res =>
      logger.warn('account-subscribe', res.error)
    )
  }

  public static initialLoad(accounts: UserAccount[]): void {
    accounts.forEach((a: UserAccount) => {
      Accounts.users.set(a.id, a)
    })
  }

  public static accounts(): UserAccount[] {
    return [...Accounts.users.values()]
  }

  /**
   * Pull new users from companies and add them to the user’s profile.
   */
  private static extractNewUsers(companyId: DataObjectId): void {
    const box = Sync.get({companyId})
    if (!box || box.companyId === Sync.profileId) {
      return
    }
    box.getAllUsers(false).forEach((u: User) => {
      Accounts.addUserToProfileIfMissing(u)
    })
    SyncEvents.addListener(box.companyId, rootNodeId, () => {
      Accounts.checkAndSyncNewUsers(box)
    })
  }

  public static onConnected(): void {
    Connector.onRequest('account-change', req =>
      Accounts.onAccountsFetched({...req, result: 'ok'})
    )
    if (!globals.silentMode) {
      Accounts.login()
    }

    Sync.listSandboxes().forEach(c => Accounts.extractNewUsers(c.companyId))
    if (!Accounts.newCompanyListenerId) {
      Accounts.newCompanyListenerId = SyncEvents.addNewCompanyListener(
        companyId => {
          Accounts.extractNewUsers(companyId)
          Accounts.createAccountInEveryCompany()
        }
      )
    }

    Accounts.addUsers(
      Sync.getProfile()
        .getAllUsers(true)
        .map((u: User) => u.id)
    )

    clearInterval(Accounts.fetchAllTimerId)
    if (fetchRequested.size) {
      Accounts.fetch([...fetchRequested])
    }
    Accounts.fetchAllTimerId = setInterval(() => {
      Accounts.fetch([...Accounts.users.keys()])
    }, fetchAllInterval)
  }

  public static refresh(): void {
    Connector.request('account-refresh', {}).catch(res => {
      logger.warn(res.error)
      if (res.error === 'Unauthorized') {
        Backend.report(
          'Clear cred on unauthorized after account-refresh',
          globals.currentUserId
        )
        Refresh.clearCredentials()
      }
    })
  }

  public static login(): void {
    if (Accounts.refreshTimerId) {
      clearInterval(Accounts.refreshTimerId)
    }
    Accounts.refreshTimerId = setInterval(Accounts.refresh, 10_000)
  }

  public static logout(): void {
    clearInterval(Accounts.refreshTimerId)
  }

  public static createAccountInEveryCompany(): void {
    if (globals.silentMode || SharedBoardsClient.getId()) {
      return
    }
    if (!globals.currentUserId) {
      throw new Error('Disallowed: creation of accounts for unauthorized users')
    }

    const mainUser = Sync.getProfile().get(globals.currentUserId) as User

    Sync.listSandboxes().forEach((sandbox: SandboxTree) => {
      if (!sandbox.has(globals.currentUserId)) {
        const user = new User(sandbox.companyId, mainUser.id, mainUser.email)
        sandbox.add(user, rootNodeId)
        sandbox.connect(user.id, sandbox.companyId)
      }
    })

    Sync.listSandboxes().forEach((box: SandboxTree) => {
      if (!box.isConnected(globals.currentUserId, box.companyId)) {
        box.connect(globals.currentUserId, box.companyId)
      }
    })
  }

  private static checkAndSyncNewUsers(box: SandboxTree): void {
    const profile = Sync.getProfile()
    box.getAllUsers(false).forEach((u: User) => {
      if (!profile.get(u.id)) {
        Accounts.addUserToProfileIfMissing(u)
      }
    })
  }

  /**
   * Synchronizes other user (not current user) with current user's profile.
   * This allow every user to keep track of his contacts.
   */
  private static addUserToProfileIfMissing(user: User): void {
    const profile = Sync.getProfile()
    if (!profile.get(user.id)) {
      const userCopy = new User(profile.companyId, user.id, user.email)
      profile.add(userCopy, rootNodeId)
      profile.restoreUserInProfile(userCopy.id)
      if (!Accounts.users.has(user.id)) {
        Accounts.addUsers([user.id])
      }
    }
  }

  static async toggleAdmin(
    companyId: DataObjectId,
    userId: DataObjectId
  ): Promise<void> {
    const box = Sync.get({companyId})
    if (!box) {
      logger.error('Company not found', companyId)
      return
    }
    const privilege = box.users.get(userId) || {isAdmin: false}
    const proceed = await showConfirm(
      privilege.isAdmin ? Lang.msg.askRemoveAdmin : Lang.msg.askAddAdmin
    )
    if (proceed) {
      const removingLastAdmin =
        privilege.isAdmin &&
        [...box.users.values()].filter(p => p.isAdmin).length === 1
      if (removingLastAdmin) {
        MessageBox.show(Lang.msg.removeLastAdminWarn, 'danger')
      } else {
        privilege.isAdmin = !privilege.isAdmin
        Connector.request('privileges', {
          targetCompanyId: companyId,
          targetUserId: userId,
          newPrivileges: privilege
        })
          .then(() => {
            MessageBox.show(Lang.msg.success, 'success')
            SyncEvents.trigger(box.companyId, [userId], false)
          })
          .catch(res => MessageBox.show(res.error, 'danger'))
      }
    }
  }
}
